import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DocumentViewer, Spinner } from '@repo/components';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission, constructQuery } from '@repo/shared/helpers';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getCompanyDocuments, getCompanyTasks } from 'src/api/company';
import { getDocumentTypes } from 'src/api/admin/documentTypes';
import { useBreadcrumbLevel } from '@repo/components/hooks';

export const Route = createFileRoute('/company_/$groupId/documents/$documentId')({
	component: RouteComponent,
});

function RouteComponent() {
	const { groupId, documentId } = Route.useParams();
	const navigate = useNavigate();
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const queryClient = useQueryClient();

	const query = constructQuery({ all: 'yes', includeLogs: 'true' });
	const { data: documents, isLoading: docsLoading } = useQuery({
		queryKey: ['company-documents', groupId, 'all'],
		queryFn: () => getCompanyDocuments(groupId, query),
		enabled: !userLoading,
	});

	const { data: documentTypes, isLoading: typesLoading } = useQuery({
		queryKey: ['company-document-types'],
		queryFn: () => getDocumentTypes('groups'),
	});

	const { data: tasksResponse } = useQuery({
		queryKey: ['company-tasks', groupId],
		queryFn: () => getCompanyTasks(groupId),
		enabled: !userLoading,
	});

	useBreadcrumbLevel(1, 'Documents', `/company/${groupId}/documents`);
	useBreadcrumbLevel(2, 'Document Details');

	if (userLoading || docsLoading || typesLoading) {
		return <Spinner className="text-[#1E1E1E]" />;
	}

	if (!authenticatedUser) return null;

	const document = (documents?.data || []).find((d) => d.id === documentId || d._id === documentId);
	if (!document) return null;

	const verifyPermission = getVerifyPermission(authenticatedUser.permissions);

	const handleClose = () => {
		navigate({ to: '/company/$groupId/documents', params: { groupId } });
	};

	const handleUpdate = () => {
		queryClient.invalidateQueries({ queryKey: ['company-documents', groupId] });
	};

	return (
		<DocumentViewer
			document={document}
			onClose={handleClose}
			types={documentTypes?.data || []}
			apiUrlBase={`/api/company-portal/groups/${groupId}/files`}
			onRevalidateDocument={handleUpdate}
			tasks={tasksResponse?.data || []}
			permissions={{
				metadata: verifyPermission([PERMISSIONS.companyDocumentsView]),
				fullView: verifyPermission([PERMISSIONS.companyDocumentsView]),
				sensitiveView: false,
				sensitiveEdit: false,
				sensitiveArchive: false,
				edit: false,
				archive: false,
				review: false,
				enableReview: false,
			}}
		/>
	);
}
